// Welche Module hängen in der unteren Leiste?
//
// Gespeichert am Profil, nicht im Browser: Die Leute melden sich auf wechselnden
// Geräten an (Hof-Tablet, eigenes Handy). Läge die Auswahl im localStorage, wäre
// sie nach dem Gerätewechsel weg.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";
import { modulNachId } from "@/lib/module-registry";

const SCHLUESSEL = ["pins"];

export function usePins() {
  const id = useAuth((s) => s.session?.user.id);

  return useQuery({
    queryKey: [...SCHLUESSEL, id],
    enabled: !!id,
    // Die Leiste steht auf jedem Bildschirm — sie darf nicht bei jedem
    // Seitenwechsel neu laden und flackern.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("pinned_modules")
        .eq("id", id!)
        .single();
      if (error) throw error;

      // Verwaiste Einträge aussortieren: Wird ein Modul umbenannt oder entfernt,
      // bliebe sonst ein toter Knopf in der Leiste stehen.
      return ((data?.pinned_modules ?? []) as string[]).filter((x) => !!modulNachId(x));
    },
  });
}

/**
 * Speichert die Leiste. Nimmt die FERTIGE Liste entgegen, nicht "das eine Modul,
 * das umgeschaltet werden soll".
 *
 * ══ Warum das wichtig ist ══
 * Vorher bekam die Mutation eine Modul-ID und rechnete sich die neue Liste aus
 * dem Query-Cache aus. Das ging schief, und zwar lautlos:
 *
 *   onMutate läuft VOR mutationFn. Das optimistische Update hatte das Modul also
 *   längst in den Cache geschrieben, als mutationFn ihn las — die sah es als
 *   "schon angeheftet" und nahm es wieder heraus. Der Server bekam exakt den
 *   alten Zustand zurückgeschrieben, HTTP 204, kein Fehler, keine Wirkung.
 *
 * Wer den Zielzustand übergibt, kann ihn nicht zweimal ausrechnen.
 */
export function useSpeicherePins() {
  const qc = useQueryClient();
  const id = useAuth((s) => s.session?.user.id);
  const key = [...SCHLUESSEL, id];

  return useMutation({
    mutationFn: async (neu: string[]) => {
      const { error } = await supabase
        .from("profiles")
        .update({ pinned_modules: neu })
        .eq("id", id!);
      if (error) throw error;
      return neu;
    },

    // Anheften muss sich sofort anfühlen. Die Leiste springt, bevor der Server
    // antwortet — und springt zurück, wenn er ablehnt.
    onMutate: async (neu) => {
      await qc.cancelQueries({ queryKey: key });
      const vorher = qc.getQueryData<string[]>(key) ?? [];
      qc.setQueryData(key, neu);
      return { vorher };
    },

    onError: (_e, _v, ctx) => {
      if (ctx?.vorher) qc.setQueryData(key, ctx.vorher);
    },

    onSuccess: (neu) => qc.setQueryData(key, neu),
  });
}
